import { useEffect, useMemo, useState } from "react";
import { api, formatError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { Plus, Pencil, Trash2, MapPin, Route as RouteIcon, ChevronDown, ArrowRightLeft } from "lucide-react";
import { toast } from "sonner";
const empty = {
  name: "",
  owner_name: "",
  phone: "",
  address: "",
  store_type: "retail",
  lat: "",
  lng: "",
  notes: "",
};

export default function Stores() {
  const [items, setItems] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [groupByRoute, setGroupByRoute] = useState(true);
  const [openGroups, setOpenGroups] = useState({});

  const load = async () => {
    const [s, r] = await Promise.all([api.get("/stores"), api.get("/routes")]);
    setItems(s.data);
    setRoutes(r.data);
  };
  useEffect(() => {
    load();
  }, []);

  // Build route-grouped structure: each route → its stores; plus an "Unassigned" bucket
  const groups = useMemo(() => {
    const storeById = new Map(items.map((s) => [s.id, s]));
    const used = new Set();
    const out = routes.map((r) => {
      const storesInRoute = (r.store_ids || [])
        .map((sid) => storeById.get(sid))
        .filter(Boolean);
      storesInRoute.forEach((s) => used.add(s.id));
      return {
        id: r.id,
        name: r.name,
        salesman: r.salesman?.name || "Unassigned",
        scheduled_date: r.scheduled_date,
        stores: storesInRoute,
      };
    });
    const unassigned = items.filter((s) => !used.has(s.id));
    if (unassigned.length > 0) {
      out.push({
        id: "__unassigned",
        name: "Unassigned (not on any route)",
        salesman: "—",
        scheduled_date: null,
        stores: unassigned,
      });
    }
    return out;
  }, [items, routes]);

  const toggleGroup = (id) =>
    setOpenGroups((g) => ({ ...g, [id]: g[id] === undefined ? false : !g[id] }));
  const isOpen = (id) => openGroups[id] !== false; // default open

  const bulkAssign = async (routeId, storeIds) => {
    const route = routes.find((r) => r.id === routeId);
    if (!route) return;
    const merged = Array.from(new Set([...(route.store_ids || []), ...storeIds]));
    try {
      await api.put(`/routes/${routeId}`, {
        name: route.name,
        salesman_id: route.salesman_id,
        store_ids: merged,
        scheduled_date: route.scheduled_date || null,
        notes: route.notes || null,
      });
      toast.success(`Assigned ${storeIds.length} stores to ${route.name}`);
      load();
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const moveStore = async (storeId, fromRouteId, toRouteId) => {
    if (fromRouteId === toRouteId) return;
    try {
      if (fromRouteId && fromRouteId !== "__unassigned") {
        const from = routes.find((r) => r.id === fromRouteId);
        if (from) {
          await api.put(`/routes/${fromRouteId}`, {
            name: from.name,
            salesman_id: from.salesman_id,
            store_ids: (from.store_ids || []).filter((x) => x !== storeId),
            scheduled_date: from.scheduled_date || null,
            notes: from.notes || null,
          });
        }
      }
      if (toRouteId === "__remove") {
        toast.success("Store removed from route");
        load();
        return;
      }
      const to = routes.find((r) => r.id === toRouteId);
      if (to) {
        const merged = Array.from(new Set([...(to.store_ids || []), storeId]));
        await api.put(`/routes/${toRouteId}`, {
          name: to.name,
          salesman_id: to.salesman_id,
          store_ids: merged,
          scheduled_date: to.scheduled_date || null,
          notes: to.notes || null,
        });
        toast.success(`Moved to ${to.name}`);
      }
      load();
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const moveAllStores = async (fromRouteId, toRouteId) => {
    if (fromRouteId === toRouteId) return;
    try {
      const from = routes.find((r) => r.id === fromRouteId);
      if (!from) return;
      const ids = [...(from.store_ids || [])];
      if (ids.length === 0) return;
      await api.put(`/routes/${fromRouteId}`, {
        name: from.name,
        salesman_id: from.salesman_id,
        store_ids: [],
        scheduled_date: from.scheduled_date || null,
        notes: from.notes || null,
      });
      const to = routes.find((r) => r.id === toRouteId);
      if (to) {
        const merged = Array.from(new Set([...(to.store_ids || []), ...ids]));
        await api.put(`/routes/${toRouteId}`, {
          name: to.name,
          salesman_id: to.salesman_id,
          store_ids: merged,
          scheduled_date: to.scheduled_date || null,
          notes: to.notes || null,
        });
      }
      toast.success(`Moved ${ids.length} store${ids.length === 1 ? "" : "s"} to ${to?.name}`);
      load();
    } catch (e) {
      toast.error(formatError(e));
    }
  };


  const openNew = () => {
    setForm(empty);
    setEditingId(null);
    setOpen(true);
  };
  const openEdit = (s) => {
    setForm({
      name: s.name,
      owner_name: s.owner_name || "",
      phone: s.phone || "",
      address: s.address || "",
      store_type: s.store_type,
      lat: s.lat ?? "",
      lng: s.lng ?? "",
      notes: s.notes || "",
    });
    setEditingId(s.id);
    setOpen(true);
  };

  const save = async () => {
    try {
      const payload = {
        ...form,
        lat: form.lat === "" ? null : Number(form.lat),
        lng: form.lng === "" ? null : Number(form.lng),
      };
      if (editingId) {
        await api.put(`/stores/${editingId}`, payload);
        toast.success("Store updated");
      } else {
        await api.post("/stores", payload);
        toast.success("Store added");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this store?")) return;
    try {
      await api.delete(`/stores/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) return toast.error("Geolocation not available");
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        setForm((f) => ({
          ...f,
          lat: pos.coords.latitude.toFixed(6),
          lng: pos.coords.longitude.toFixed(6),
        })),
      () => toast.error("Failed to get location"),
    );
  };

  return (
    <div className="space-y-5" data-testid="admin-stores-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="overline text-[color:var(--muted)]">Network</p>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Stores</h1>
          <p className="text-[color:var(--muted)] mt-1">Retail & wholesale outlets in your territory.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openNew} className="bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white" data-testid="add-store-button">
              <Plus className="h-4 w-4 mr-1" /> Add store
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg" data-testid="store-dialog">
            <DialogHeader>
              <DialogTitle>{editingId ? "Edit store" : "New store"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Store name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="store-name-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Owner</Label>
                  <Input value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })} />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                </div>
              </div>
              <div>
                <Label>Address</Label>
                <Textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="store-address-input" />
              </div>
              <div>
                <Label>Type</Label>
                <Select value={form.store_type} onValueChange={(v) => setForm({ ...form, store_type: v })}>
                  <SelectTrigger data-testid="store-type-trigger"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="retail">Retail</SelectItem>
                    <SelectItem value="wholesale">Wholesale</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Latitude</Label>
                  <Input value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })} />
                </div>
                <div>
                  <Label>Longitude</Label>
                  <Input value={form.lng} onChange={(e) => setForm({ ...form, lng: e.target.value })} />
                </div>
              </div>
              <Button type="button" variant="outline" onClick={useMyLocation} className="w-full" data-testid="use-location-button">
                <MapPin className="h-4 w-4 mr-2" /> Use current location
              </Button>
            </div>
            <DialogFooter>
              <Button onClick={save} className="bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white" data-testid="save-store-button">
                Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex items-center gap-2 text-sm">
        <button
          type="button"
          onClick={() => setGroupByRoute(true)}
          className={`px-3 py-1.5 rounded-md ${
            groupByRoute
              ? "bg-[color:var(--ink)] text-white"
              : "bg-[color:var(--surface-2)] text-[color:var(--muted)]"
          }`}
          data-testid="stores-group-by-route"
        >
          Group by route
        </button>
        <button
          type="button"
          onClick={() => setGroupByRoute(false)}
          className={`px-3 py-1.5 rounded-md ${
            !groupByRoute
              ? "bg-[color:var(--ink)] text-white"
              : "bg-[color:var(--surface-2)] text-[color:var(--muted)]"
          }`}
          data-testid="stores-flat-list"
        >
          Flat list
        </button>
        <span className="text-xs text-[color:var(--muted)] ml-2">
          {items.length} stores · {routes.length} routes
        </span>
      </div>

      {groupByRoute ? (
        <div className="space-y-4" data-testid="stores-grouped-view">
          {groups.length === 0 && (
            <div className="rounded-xl border border-dashed border-[color:var(--border)] py-10 text-center text-[color:var(--muted)]">
              No stores yet.
            </div>
          )}
          {groups.map((g) => {
            const expanded = isOpen(g.id);
            return (
              <div
                key={g.id}
                className="rounded-xl border border-[color:var(--border)] bg-white ink-shadow overflow-hidden"
                data-testid={`stores-group-${g.id}`}
              >
                <button
                  type="button"
                  onClick={() => toggleGroup(g.id)}
                  className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-[color:var(--surface-2)]/50"
                >
                  <div
                    className={`h-10 w-10 rounded-lg flex items-center justify-center shrink-0 ${
                      g.id === "__unassigned"
                        ? "bg-[color:var(--surface-2)] text-[color:var(--muted)]"
                        : "bg-[color:var(--primary)]/10 text-[color:var(--primary)]"
                    }`}
                  >
                    <RouteIcon className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-lg font-bold truncate">
                      {g.name}
                    </div>
                    <div className="text-xs text-[color:var(--muted)] flex items-center gap-2">
                      <span>{g.salesman}</span>
                      {g.scheduled_date && <span>· {g.scheduled_date}</span>}
                      <span>· {g.stores.length} store{g.stores.length === 1 ? "" : "s"}</span>
                    </div>
                  </div>
                  {g.id === "__unassigned" && g.stores.length > 0 && routes.length > 0 && (
                    <div onClick={(e) => e.stopPropagation()}>
                      <Select
                        onValueChange={(rid) => bulkAssign(rid, g.stores.map((s) => s.id))}
                      >
                        <SelectTrigger
                          className="h-9 w-[220px] bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white border-0"
                          data-testid="bulk-assign-trigger"
                        >
                          <SelectValue placeholder={`Assign all ${g.stores.length} to route…`} />
                        </SelectTrigger>
                        <SelectContent>
                          {routes.map((r) => (
                            <SelectItem key={r.id} value={r.id} data-testid={`bulk-assign-${r.id}`}>
                              {r.name} ({r.salesman?.name || "unassigned"})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {g.id !== "__unassigned" && g.stores.length > 0 && routes.length > 1 && (
                    <div onClick={(e) => e.stopPropagation()}>
                      <Select
                        onValueChange={(rid) => moveAllStores(g.id, rid)}
                      >
                        <SelectTrigger
                          className="h-9 w-[200px] border-[color:var(--border)]"
                          data-testid={`move-all-trigger-${g.id}`}
                        >
                          <SelectValue placeholder="Move all to…" />
                        </SelectTrigger>
                        <SelectContent>
                          {routes
                            .filter((r) => r.id !== g.id)
                            .map((r) => (
                              <SelectItem key={r.id} value={r.id}>
                                {r.name} ({r.salesman?.name || "unassigned"})
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <ChevronDown
                    className={`h-5 w-5 text-[color:var(--muted)] shrink-0 transition-transform ${
                      expanded ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {expanded && (
                  <div className="overflow-x-auto border-t border-[color:var(--border)]">
                    <table className="w-full text-sm">
                      <thead className="bg-[color:var(--surface-2)]">
                        <tr className="text-left text-[color:var(--muted)] uppercase text-xs">
                          <th className="px-4 py-2 w-10">#</th>
                          <th className="px-4 py-2">Name</th>
                          <th className="px-4 py-2">Type</th>
                          <th className="px-4 py-2">Owner</th>
                          <th className="px-4 py-2">Phone</th>
                          <th className="px-4 py-2">Address</th>
                          <th className="px-4 py-2 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {g.stores.length === 0 && (
                          <tr>
                            <td colSpan="7" className="py-6 text-center text-[color:var(--muted)]">
                              No stores in this route.
                            </td>
                          </tr>
                        )}
                        {g.stores.map((s, i) => (
                          <tr
                            key={s.id}
                            className="border-t border-[color:var(--border)]"
                            data-testid={`store-row-${s.id}`}
                          >
                            <td className="px-4 py-3 text-[color:var(--muted)]">{i + 1}</td>
                            <td className="px-4 py-3 font-medium">{s.name}</td>
                            <td className="px-4 py-3">
                              <span
                                className={`text-xs px-2 py-0.5 rounded-full ${
                                  s.store_type === "wholesale"
                                    ? "bg-[color:var(--mustard)]/20 text-[#9A742E]"
                                    : "bg-[color:var(--moss)]/10 text-[color:var(--moss)]"
                                }`}
                              >
                                {s.store_type}
                              </span>
                            </td>
                            <td className="px-4 py-3">{s.owner_name || "—"}</td>
                            <td className="px-4 py-3">{s.phone || "—"}</td>
                            <td className="px-4 py-3 text-[color:var(--muted)] max-w-xs truncate">
                              {s.address}
                            </td>
                            <td className="px-4 py-3 text-right whitespace-nowrap">
                              {routes.length > 1 && g.id !== "__unassigned" && (
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <button
                                      className="p-1 text-[color:var(--muted)] hover:text-[color:var(--ink)] mr-1"
                                      title="Move to another route"
                                      data-testid={`move-store-${s.id}`}
                                    >
                                      <ArrowRightLeft className="h-4 w-4" />
                                    </button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end" className="w-56">
                                    <DropdownMenuLabel>Move to route</DropdownMenuLabel>
                                    <DropdownMenuSeparator />
                                    {routes
                                      .filter((rt) => rt.id !== g.id)
                                      .map((rt) => (
                                        <DropdownMenuItem
                                          key={rt.id}
                                          onSelect={() => moveStore(s.id, g.id, rt.id)}
                                          data-testid={`move-store-${s.id}-to-${rt.id}`}
                                        >
                                          {rt.name}
                                          <span className="ml-auto text-xs text-[color:var(--muted)]">
                                            {rt.salesman?.name || "—"}
                                          </span>
                                        </DropdownMenuItem>
                                      ))}
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                      onSelect={() => moveStore(s.id, g.id, "__remove")}
                                      className="text-[color:var(--primary)]"
                                      data-testid={`remove-from-route-${s.id}`}
                                    >
                                      Remove from route
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              )}
                              <button
                                onClick={() => openEdit(s)}
                                className="p-1 text-[color:var(--muted)] hover:text-[color:var(--ink)]"
                                data-testid={`edit-store-${s.id}`}
                              >
                                <Pencil className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => remove(s.id)}
                                className="p-1 ml-1 text-[color:var(--muted)] hover:text-[color:var(--primary)]"
                                data-testid={`delete-store-${s.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="rounded-xl border border-[color:var(--border)] bg-white ink-shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[color:var(--surface-2)]">
                <tr className="text-left text-[color:var(--muted)] uppercase text-xs">
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Owner</th>
                  <th className="px-4 py-3">Phone</th>
                  <th className="px-4 py-3">Address</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((s) => (
                  <tr key={s.id} className="border-t border-[color:var(--border)]">
                    <td className="px-4 py-3 font-medium">{s.name}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full ${
                          s.store_type === "wholesale"
                            ? "bg-[color:var(--mustard)]/20 text-[#9A742E]"
                            : "bg-[color:var(--moss)]/10 text-[color:var(--moss)]"
                        }`}
                      >
                        {s.store_type}
                      </span>
                    </td>
                    <td className="px-4 py-3">{s.owner_name || "—"}</td>
                    <td className="px-4 py-3">{s.phone || "—"}</td>
                    <td className="px-4 py-3 text-[color:var(--muted)] max-w-xs truncate">
                      {s.address}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => openEdit(s)} className="p-1 text-[color:var(--muted)] hover:text-[color:var(--ink)]">
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button onClick={() => remove(s.id)} className="p-1 ml-1 text-[color:var(--muted)] hover:text-[color:var(--primary)]">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
                {items.length === 0 && (
                  <tr>
                    <td colSpan="6" className="py-10 text-center text-[color:var(--muted)]">
                      No stores yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
